import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Flame, Save, RefreshCw, Power } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const ThunderCashSettingsAdminTab = () => {
    const [cfg, setCfg] = useState(null);
    const [enabled, setEnabled] = useState(true);
    const [levelsStr, setLevelsStr] = useState('');
    const [maxWin, setMaxWin] = useState('');
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);

    const apply = (c) => {
        setCfg(c);
        setEnabled(!!c.enabled);
        setLevelsStr((c.bet_levels || []).join(', '));
        setMaxWin(String(c.max_win_per_round));
    };

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/thunder-cash/settings`, { withCredentials: true });
            apply(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load Thunder Cash settings');
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        const bet_levels = levelsStr.split(',').map((s) => parseFloat(s.trim())).filter((n) => !isNaN(n) && n > 0);
        if (!bet_levels.length) { toast.error('Enter at least one positive bet level'); return; }
        const mw = parseFloat(maxWin);
        if (isNaN(mw) || mw <= 0) { toast.error('Max win must be greater than 0'); return; }
        setSaving(true);
        try {
            const { data } = await axios.put(`${API}/admin/thunder-cash/settings`,
                { enabled, bet_levels, max_win_per_round: mw }, { withCredentials: true });
            apply(data);
            toast.success('Thunder Cash settings saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    if (!cfg) return <div className="text-[#A0AAB2] py-12 text-center">Loading Thunder Cash settings…</div>;

    return (
        <div className="card-dark p-5 space-y-4" data-testid="tc-settings-admin">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-[#ff8a1e]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Thunder Cash — Settings</h3>
                </div>
                <Button size="sm" variant="ghost" onClick={load} className="text-[#A0AAB2] h-8 text-xs" data-testid="tc-settings-refresh">
                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-2">
                    <Power className={`w-4 h-4 ${enabled ? 'text-[#3ad07a]' : 'text-[#ff5a5a]'}`} />
                    <div>
                        <Label className="text-white text-sm">Game enabled</Label>
                        <p className="text-[10px] text-[#A0AAB2]">When off, new sessions and spins are blocked for all players.</p>
                    </div>
                </div>
                <Switch checked={enabled} onCheckedChange={setEnabled}
                    className="data-[state=checked]:bg-[#3ad07a] data-[state=unchecked]:bg-white/20"
                    data-testid="tc-settings-enabled" />
            </div>

            <div>
                <Label className="text-white text-xs">Bet levels (dollars, comma-separated)</Label>
                <Input value={levelsStr} onChange={(e) => setLevelsStr(e.target.value)}
                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono"
                    placeholder="0.5, 1, 2, 5, 10, 20" data-testid="tc-settings-betlevels" />
                <p className="text-[9px] text-[#A0AAB2] mt-1">These are the exact bet buttons players can choose. Values are validated on every spin.</p>
            </div>

            <div>
                <Label className="text-white text-xs">Max win per round (dollars)</Label>
                <Input type="number" step="100" min="1" value={maxWin} onChange={(e) => setMaxWin(e.target.value)}
                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono max-w-[220px]"
                    data-testid="tc-settings-maxwin" />
                <p className="text-[9px] text-[#A0AAB2] mt-1">Hard cap on a single round's REGULAR win. Progressive jackpot pool prizes are exempt and can exceed this. Raise here to open higher limits.</p>
            </div>

            <Button onClick={save} disabled={saving || loading}
                className="bg-[#ff8a1e] text-black hover:bg-[#ff8a1e]/90 font-bold"
                data-testid="tc-settings-save">
                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Settings'}
            </Button>
        </div>
    );
};

export default ThunderCashSettingsAdminTab;
